import React from "react";

// Structured per-service result for a V3 deploy (FRONTEND_DEPLOYMENT_V3.md §5).
// Renders a card per unit/api (repository, action, branch, commit, runs/pipeline,
// values, Anypoint state, default-branch switch, service connections, changed files)
// plus a header (technology/mode/platform), typed warnings, and a footer.

const C = {
  panel: "#121825", border: "rgba(255,255,255,0.08)", accent: "#3b7fff",
  text: "#dce8ff", sub: "#aac0dc", muted: "#7a93bc",
  good: "#00c98d", warn: "#f5a623", bad: "#ff6b6b",
};

// Warning type → { label, severity }. severity: action | attention | info.
const WARNING_META = {
  DEFAULT_BRANCH_NOT_RESTORED: { label: "Default branch not restored — action needed", severity: "action" },
  MISSING_SECURE_KEY: { label: "Add MULE_SECURE_KEY / MULE_ENCRYPTION_KEY as secret variables", severity: "attention" },
  NEXUS_CREDENTIALS_MISSING: { label: "MuleSoft EE Maven (Nexus) credentials missing", severity: "attention" },
  NEXUS_FROM_SERVER_ENV: { label: "Nexus credentials came from the server env — send them explicitly", severity: "attention" },
  MISSING_VALUES: { label: "Some values the workflows need are not set", severity: "attention" },
  VALUES_IGNORED: { label: "Some sent values are not read by the workflows", severity: "info" },
  DISPATCH_FALLBACK: { label: "Workflow started via push on the feature branch", severity: "info" },
  DEFAULT_BRANCH_SWITCHED: { label: "Default branch was switched briefly, then restored", severity: "info" },
  LEGACY_SECURE_KEY: { label: "Pipeline still hard-codes a secure key", severity: "info" },
};
const SEV_COLOR = { action: C.bad, attention: C.warn, info: C.muted };

const chip = (bg, color) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: bg, color, marginRight: 6, marginTop: 4 });
const short = (s) => (s && String(s).length > 10 ? String(s).slice(0, 10) : s);

function Pushed({ pushed }) {
  if (pushed === null) return <span style={{ color: C.muted }}>no changes</span>;
  if (!pushed) return null;
  return <span style={{ color: C.sub }}>{pushed.added || 0} added · {pushed.changed || 0} changed</span>;
}

function Runs({ unit }) {
  const runs = unit.runs || (unit.run ? [unit.run] : []);
  const pipe = unit.pipeline;
  return (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      {pipe && (
        <div style={{ color: C.sub }}>
          Pipeline: {pipe.url ? <a href={pipe.url} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{pipe.name || "run"}</a> : (pipe.name || "run")}
          {unit.run && unit.run.result ? ` · ${unit.run.result}` : ""}
        </div>
      )}
      {runs.map((r, i) => (
        <div key={i} style={{ color: C.sub }}>
          Run: {r.url ? <a href={r.url} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{r.conclusion || r.status || "run"}</a> : (r.conclusion || r.result || r.status || "run")}
        </div>
      ))}
    </div>
  );
}

function Values({ values }) {
  if (!values) return null;
  const { setFromRequest = [], setByIntelog = [], missingAtStart = [] } = values;
  if (!setFromRequest.length && !setByIntelog.length && !missingAtStart.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      {setFromRequest.map((v) => <span key={`r${v}`} style={chip("rgba(0,201,141,0.12)", C.good)}>{v}</span>)}
      {setByIntelog.map((v) => <span key={`i${v}`} style={chip("rgba(59,127,255,0.12)", C.accent)}>{v}</span>)}
      {missingAtStart.map((v) => <span key={`m${v}`} style={chip("rgba(255,107,107,0.12)", C.bad)}>⚠ {v}</span>)}
    </div>
  );
}

function Anypoint({ anypoint }) {
  if (!anypoint) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: C.sub }}>
      <div>Anypoint: {anypoint.publishedVersion || "—"}{anypoint.designCenter ? ` · ${anypoint.designCenter}` : ""}{anypoint.ramlChanged === false ? " · spec unchanged" : ""}</div>
      {(anypoint.environments || []).map((e, i) => (
        <div key={i} style={{ color: C.muted }}>{e.key}: {e.instance}{e.instanceId ? ` (${e.instanceId})` : ""}</div>
      ))}
    </div>
  );
}

function UnitCard({ u }) {
  const ok = u.success !== false;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 10, background: "rgba(255,255,255,0.02)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>
          <span style={{ fontFamily: "monospace", color: C.accent }}>{u.service || u.name}</span>
          {u.repository && <span style={{ color: C.muted, fontWeight: 400 }}> · {u.url ? <a href={u.url} target="_blank" rel="noreferrer" style={{ color: C.sub }}>{u.repository}</a> : u.repository}</span>}
        </div>
        <span style={{ ...chip(ok ? "rgba(0,201,141,0.15)" : "rgba(255,107,107,0.15)", ok ? C.good : C.bad), marginTop: 0 }}>{ok ? "✓ success" : "✗ failed"}</span>
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: C.sub }}>
        {u.action || "deployed"}{u.branch ? ` · ${u.branch}` : ""}{u.branchCreated ? " (created)" : ""}{u.commit ? ` · ${short(u.commit)}` : ""} <Pushed pushed={u.pushed} />
      </div>
      {u.error && <div style={{ marginTop: 4, fontSize: 12, color: C.bad }}>{u.error}</div>}
      {u.defaultBranchSwitch && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: u.defaultBranchSwitch.restored === false ? C.bad : C.muted }}>
          Default branch temporarily → {u.defaultBranchSwitch.to}{u.defaultBranchSwitch.restored === false ? " — NOT restored, set it back manually" : " (restored)"}
        </div>
      )}
      {Array.isArray(u.serviceConnections) && u.serviceConnections.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 11.5, color: C.muted }}>
          Service connections: {u.serviceConnections.map((s) => `${s.name} (${s.action})`).join(", ")}
        </div>
      )}
      {Array.isArray(u.changedFiles) && u.changedFiles.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 11, color: C.muted }}>Intelog committed: {u.changedFiles.join(", ")}</div>
      )}
      <Anypoint anypoint={u.anypoint} />
      <Values values={u.values} />
      <Runs unit={u} />
    </div>
  );
}

export default function DeploymentResult({ data }) {
  if (!data) return null;
  const units = data.units || data.apis || [];
  const warnings = data.warnings || [];
  const gen = data.generated || [];
  const up = data.uploaded || [];
  const totalMin = data.metrics && data.metrics.totalTime ? Math.round(data.metrics.totalTime / 60000) : null;

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: data.success === false ? C.bad : C.good }}>
          {data.success === false ? "Deployment finished with errors" : "Deployment result"}
        </span>
        {data.technology && <span style={chip("rgba(255,255,255,0.06)", C.sub)}>{data.technology}</span>}
        {data.mode && <span style={chip("rgba(255,255,255,0.06)", C.sub)}>{data.mode}</span>}
        {data.platform && <span style={chip("rgba(255,255,255,0.06)", C.sub)}>{data.platform}</span>}
      </div>

      {warnings.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {warnings.map((w, i) => {
            const type = typeof w === "string" ? null : w.type;
            const meta = type && WARNING_META[type];
            const sev = meta ? meta.severity : "info";
            const color = SEV_COLOR[sev];
            const text = meta ? meta.label : (typeof w === "string" ? w : (w.message || w.type));
            return (
              <div key={i} style={{ fontSize: 12, color, padding: "6px 10px", borderRadius: 6, marginBottom: 4, background: sev === "action" ? "rgba(255,107,107,0.1)" : sev === "attention" ? "rgba(245,166,35,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${sev === "action" ? "rgba(255,107,107,0.35)" : "transparent"}` }}>
                {sev === "action" ? "❗ " : sev === "attention" ? "⚠ " : "• "}{text}{meta && typeof w !== "string" && w.message ? ` — ${w.message}` : ""}
              </div>
            );
          })}
        </div>
      )}

      {units.map((u, i) => <UnitCard key={i} u={u} />)}

      {(gen.length > 0 || up.length > 0) && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>
          {gen.length > 0 && <div>Generated workflow{gen.length > 1 ? "s" : ""}: {gen.map((g) => `${g.service} (${(g.files || []).join(", ")})`).join("; ")}</div>}
          {up.length > 0 && <div>Uploaded: {up.map((g) => `${g.service} (${g.file})`).join("; ")}</div>}
        </div>
      )}
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
        {data.packagesRepository ? `Packages repository: ${data.packagesRepository}` : ""}{data.packagesRepository && totalMin ? " · " : ""}{totalMin != null ? `Total ${totalMin} min` : ""}
      </div>
    </div>
  );
}
